import { useState, useEffect } from "react";
import { useModal } from "../../hooks/useModal";
import { Modal } from "../ui/modal";
import Button from "../ui/button/Button";
import Input from "../form/input/InputField";
import Label from "../form/Label";
import Alert from "../ui/alert/Alert";
import { ErrorIcon, PencilIcon } from "../../icons";
import { cache, request } from "../../utils";

interface WebhookPayload {
  webhookUrl: string;
}


export default function AddWebhookUrl() {
  // Modal controls
  const {
    isOpen: isWebhookModalOpen,
    openModal: openWebhookModal,
    closeModal: closeWebhookModal,
  } = useModal();

  // Form state
  const [webhookUrl, setWebhookUrl] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Load user data from cache on component mount
  useEffect(() => {
    const loadUserData = () => {
      try {
        const userData: any = cache.get("USER");
        
        if (userData) {
          setWebhookUrl(userData.webhookUrl || "");

        }
      } catch (err) {
        console.error("Error loading user data:", err);
      }
    };

    loadUserData();
  }, []);

  // Reset form fields
  const resetForm = () => {
    setInputValue("");
    setError("");
    setSuccess(false);
  };

  const handleCloseModal = () => {
    resetForm();
    closeWebhookModal();
  };

  // Save webhook URL
  const handleSaveWebhook = async () => {
    if (!inputValue.trim()) {
      setError("Please enter a valid webhook URL");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await request.patch<WebhookPayload>({
        url: `/user/add-webhook-url`,
        data: { url: inputValue.trim() }
      });

      const newWebhookUrl = response.data?.webhookUrl;

      if (!newWebhookUrl) {
        setError("Failed to update webhook URL");
        return;
      }
      setWebhookUrl(newWebhookUrl);
      
      // Update cached user data
      const cachedUser = cache.get("USER");
      if (cachedUser) {
        const updatedUser = {
          ...cachedUser,
          webhookUrl: newWebhookUrl,
        };
        cache.set("USER", updatedUser);
      }

      setSuccess(true);
      setTimeout(() => {
        closeWebhookModal();
        resetForm();
      }, 1500);
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = () => {
    setInputValue(webhookUrl);
    setError("");
    openWebhookModal();
  };

  const openCreateModal = () => {
    setInputValue("");
    setError("");
    openWebhookModal();
  };

  // const copyToClipboard = () => {
  //   if (!webhookUrl) return;

  //   navigator.clipboard.writeText(webhookUrl);
  //   setCopied(true);
  //   setTimeout(() => setCopied(false), 3000);
  // };

  return (
    <>
      <div className="p-5 border border-gray-200 rounded-2xl dark:border-gray-800 lg:p-6">
        <div className="flex flex-col items-center gap-6 lg:flex-row lg:justify-between">
          <div className="w-full text-center lg:text-left lg:w-auto">
            <h4 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              Webhook URL
            </h4>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {webhookUrl ? "Configure your webhook url" : "Set up webhook notifications"}
            </p>
          </div>

          {webhookUrl ? (
            <button
              onClick={openEditModal}
              className="flex w-full items-center justify-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200 lg:inline-flex lg:w-auto"
            >
              <PencilIcon className="size-4" />
              Edit URL
            </button>
          ) : (
            <button
              onClick={openCreateModal}
              className="flex w-full items-center justify-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200 lg:inline-flex lg:w-auto"
            >
              Add Webhook
            </button>
          )}
        </div>

        {webhookUrl && (
          <div className="mt-6">
            <div className="flex items-center gap-4">
              <div className="flex-1 p-3 rounded-lg border border-gray-300 bg-gray-50 dark:border-gray-700 dark:bg-gray-800 min-h-[42px] flex items-center">
                <span className="font-mono text-gray-800 dark:text-white/90 break-all flex-1 overflow-x-auto pr-2">
                  {webhookUrl}
                </span>
                {/* <button
                  onClick={copyToClipboard}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 ml-2"
                  title="Copy to clipboard"
                >
                  {copied ? (
                    <span className="text-sm text-brand-500">Copied!</span>
                  ) : (
                    <CopyIcon className="fill-current size-5" />
                  )}
                </button> */}
              </div>
            </div>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Your webhook url for receiving notifications and events.
            </p>
          </div>
        )}
      </div>

      {/* Webhook URL Modal */}
      <Modal
        isOpen={isWebhookModalOpen}
        onClose={handleCloseModal}
        className="max-w-[700px] m-4"
      >
        <div className="relative w-full p-4 overflow-y-auto bg-white no-scrollbar rounded-3xl dark:bg-gray-900 lg:p-11">
          <div className="px-2 pr-14">
            <h4 className="mb-2 text-2xl font-semibold text-gray-800 dark:text-white/90">
              {webhookUrl ? "Edit Webhook URL" : "Add Webhook URL"}
            </h4>
            <p className="mb-6 text-sm text-gray-500 dark:text-gray-400 lg:mb-7">
              <span className="flex items-center gap-2">
                <ErrorIcon /> Enter a valid HTTPS URL to receive webhook notifications.
              </span>
            </p>
          </div>

          {success && (
            <div className="mb-6">
              <Alert
                variant="success"
                title="Success!"
                message="Webhook URL saved successfully!"
              />
            </div>
          )}

          {error && (
            <div className="mb-6">
              <Alert variant="error" title="Error" message={error} />
            </div>
          )}

          <form className="flex flex-col">
            <div className="px-2 overflow-y-auto custom-scrollbar">
              <div className="grid grid-cols-1 gap-x-6 gap-y-5">
                <div className="col-span-2">
                  <Label>Webhook URL</Label>
                  <Input
                    type="url"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder="https://your-domain.com/webhook"
                    required
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 px-2 mt-6 lg:justify-end">
              <Button size="sm" variant="outline" onClick={handleCloseModal}>
                Close
              </Button>
              <Button
                size="sm"
                onClick={handleSaveWebhook}
                disabled={loading || !inputValue.trim()}
              >
                {loading ? "Saving..." : (webhookUrl ? "Update URL" : "Save URL")}
              </Button>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
}